<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { LoyaltyLeague, LoyaltyLeagueRewards, RewardsApi } from '@/components/games/rewardsPopup/RewardsPopup.ts'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import CheckImgUrl from '@/assets/components/loyaltyGameBox/check.webp?url'
import Arrow from '@/assets/components/loyaltyProgressAnimation/arrow.svg'
import { rewardsPopup } from '@/components/games/rewardsPopup/RewardsPopup.ts'

const { t } = useI18n()
const { nextLoopEvent } = utilsPlugin
const { routeGame } = gamesUtils
const { toHumanNumber, windowWidth, debounce } = tools

const gameRewardsData = computed<RewardsApi>(() => {
  return rewardsPopup.value.rewardsApi?.[routeGame.value?.gameKey || ''] || {}
})

interface Segment {
  id?: number
  no?: number
  progress?: number
  progressMax?: number
  assetBar?: string
  asset?: string
  league?: string
  amount?: string
  taken?: number
  width?: number
  req_value?: number
}

const segments = ref<Segment[]>([])

const startPoint = ref<Segment>({})

function initProgressOptions() {
  return {
    visibleSegments: 0,
    viewportWidth: 0,
    segmentWidth: 130,
    speed: 200, // px/s
    speedSegment: 1,
    distanceX: 0,
    run: true,
    forceHideArrows: false,
    borderSegmentWidth: 40,
    takenRewardsLength: 0,
    isNextReward: false,
    nextRewardIndex: -1,
    activeSegmentMinPoints: 0,
    activeSegmentSizePoints: 0
  }
}
function initTickStepOptions() {
  return {
    lastTime: 0,
    currentStepIndex: 0,
    animationId: 0,
    xTransform: 0,
    maxTransformX: 0
  }
}

const progressOptions = ref(initProgressOptions())
const tickStepOptions = ref(initTickStepOptions())

const viewport = ref<HTMLElement | null>(null)
const lastSeg = ref<HTMLDivElement | null>(null)
const segmentsCounter = computed(() => {
  return segments.value.length
})
const trackWidth = computed(() => (segmentsCounter.value - 2) * progressOptions.value.segmentWidth + (2 * progressOptions.value.borderSegmentWidth))

const lastVisible = ref(false)
let io: IntersectionObserver | null = null

function assignLastSeg(i: number) {
  return (
    el: Element | ComponentPublicInstance | null,
    _refs?: Record<string, any>
  ) => {
    const htmlEl = el as HTMLDivElement | null
    if (i === (segmentsCounter.value - 1)) {
      lastSeg.value = htmlEl
    }
  }
}
function tickStep(tickTime: number) {
  if (!tickStepOptions.value.lastTime) {
    tickStepOptions.value.lastTime = tickTime
  }
  const offsetTime = (tickTime - tickStepOptions.value.lastTime) / 1000
  tickStepOptions.value.lastTime = tickTime

  const idxSegment = tickStepOptions.value.currentStepIndex
  const segment = segments.value[idxSegment]
  const speedCurrentSegment = segment?.width ? ((progressOptions.value.speed / segment.width) * progressOptions.value.speed) : progressOptions.value.speedSegment
  const distanceX = speedCurrentSegment * offsetTime
  const newDistance = (segments.value?.[tickStepOptions.value.currentStepIndex]?.progress || 0) + (distanceX || 0)

  if (segment) {
    segment.progress = Math.min(segment.progressMax ?? 100, newDistance > 100 ? 100 : Math.max(newDistance, (segment.progress || 0)))
  }
  progressOptions.value.distanceX += distanceX
  if (!lastVisible.value) {
    if (progressOptions.value.distanceX > 100) {
      const moduloVisibleSegments = tickStepOptions.value.currentStepIndex % progressOptions.value.visibleSegments
      const translateSpeedViewport = 0.6 + 0.7 * (moduloVisibleSegments / progressOptions.value.visibleSegments)
      const nextStep = distanceX * translateSpeedViewport
      if (nextStep > tickStepOptions.value.maxTransformX) {
        tickStepOptions.value.xTransform = tickStepOptions.value.maxTransformX * -1
      } else {
        tickStepOptions.value.xTransform -= nextStep
      }
    }
  }

  if ((segments.value?.[tickStepOptions.value.currentStepIndex]?.progress || 0) >= 100 || (segments.value?.[tickStepOptions.value.currentStepIndex]?.progress || 0) >= (segments.value?.[tickStepOptions.value.currentStepIndex]?.progressMax || 0)) {
    if (++tickStepOptions.value.currentStepIndex >= segmentsCounter.value) {
      cancelAnimationFrame(tickStepOptions.value.animationId)
      tickStepOptions.value.animationId = 0
      tickStepOptions.value.xTransform = (trackWidth.value - progressOptions.value.viewportWidth) * -1
      progressOptions.value.run = false
      return
    }
  }

  tickStepOptions.value.animationId = requestAnimationFrame(tickStep)
}
onMounted(() => {
  nextLoopEvent(100).then(() => {
    progressOptions.value.viewportWidth = viewport.value?.clientWidth || 0
    initSegments()
    if (!segmentsCounter.value) {
      return
    }
    queueMicrotask(() => {
      if (viewport.value && lastSeg.value) {
        io = new IntersectionObserver((entries) => {
          lastVisible.value = entries[0]?.isIntersecting ?? false
        }, { root: viewport.value, threshold: 1 })
        io.observe(lastSeg.value)
      }
    })
  })
})

function moveLeft() {
  tickStepOptions.value.xTransform = Math.min(0, tickStepOptions.value.xTransform + progressOptions.value.segmentWidth)
}
function moveRight() {
  tickStepOptions.value.xTransform = Math.max((trackWidth.value - progressOptions.value.viewportWidth) * -1, tickStepOptions.value.xTransform - progressOptions.value.segmentWidth)
}
function initSegments() {
  const leagues: LoyaltyLeague[] = (gameRewardsData.value?.loyaltyLeagues || []).sort((a, b) => (a?.no || 0) - (b?.no || 0))
  const leaguesMap: Record<string, LoyaltyLeague> = {}
  const segmentList: Segment[] = []
  if (progressOptions.value.viewportWidth > 500) {
    progressOptions.value.borderSegmentWidth = 2 * progressOptions.value.borderSegmentWidth
  }
  leagues.forEach((league) => {
    leaguesMap[league.name || ''] = league
    const rewardLeague: LoyaltyLeagueRewards[] = (gameRewardsData.value?.loyaltyLeagueRewards?.[league?.name || ''] || []).sort((a, b) => (a?.no || 0) - (b?.no || 0))
    rewardLeague.forEach((reward) => {
      if (reward?.id && league?.name) {
        segmentList.push({
          id: reward.id,
          no: reward?.no || 0,
          progress: 0,
          progressMax: 100,
          league: league.name,
          assetBar: league?.assetBar && league.assetBar.substring(0, 4) !== 'http' ? (routeGame.value?.imgStorageUrl || '') + league.assetBar : league.assetBar,
          asset: reward?.items?.[0]?.asset && reward?.items?.[0]?.asset?.substring(0, 4) !== 'http' ? (routeGame.value?.imgStorageUrl || '') + reward.items[0].asset : reward.items?.[0]?.asset,
          amount: toHumanNumber(reward?.items?.[0]?.amount || ''),
          taken: reward?.taken || 0,
          req_value: reward?.req_value || 0
        })
      }
    })
  })
  progressOptions.value.takenRewardsLength = segmentList.length
  const currentLeague = rewardsPopup.value.loyaltyLeaguesPlayer?.[routeGame.value?.gameKey || '']?.league || ''

  progressOptions.value.nextRewardIndex = (rewardsPopup.value.loyaltyLeagueRewards?.[routeGame.value?.gameKey || '']?.[currentLeague] || []).findIndex(reward => reward?.taken === 0)
  const nextReward = rewardsPopup.value.loyaltyLeagueRewards?.[routeGame.value?.gameKey || '']?.[currentLeague]?.[progressOptions.value.nextRewardIndex]
  const leftBorderedSegment = {
    width: progressOptions.value.borderSegmentWidth,
    league: segmentList[0]?.league,
    assetBar: segmentList[0]?.assetBar || '',
    progressMax: progressOptions.value.takenRewardsLength ? 100 : 0
  }

  startPoint.value = {}
  segments.value = []
  const firstSegment = segmentList[0]
  if (firstSegment) {
    startPoint.value = firstSegment
    segments.value = segmentList.slice(1)
  } else {
    segments.value.push(...segmentList)
  }

  if (nextReward) {
    if (!leaguesMap[currentLeague]?.name) {
      const currentLeagueData = (rewardsPopup.value.loyaltyCurrentLeagues?.[routeGame.value?.gameKey || ''] || []).find(league => league?.name === currentLeague)
      if (currentLeagueData) {
        leaguesMap[currentLeague] = currentLeagueData
      }
    }
    progressOptions.value.isNextReward = true
    progressOptions.value.activeSegmentMinPoints = rewardsPopup.value.loyaltyLeagueRewards?.[routeGame.value?.gameKey || '']?.[currentLeague]?.[progressOptions.value.nextRewardIndex - 1]?.req_value || 0
    progressOptions.value.activeSegmentSizePoints = (nextReward?.req_value || 0) - progressOptions.value.activeSegmentMinPoints
    segments.value.push({
      id: nextReward.id,
      no: nextReward?.no || 0,
      progress: 0,
      progressMax: 0,
      league: leaguesMap[currentLeague]?.name || '',
      assetBar: leaguesMap[currentLeague]?.assetBar && leaguesMap[currentLeague].assetBar.substring(0, 4) !== 'http' ? (routeGame.value?.imgStorageUrl || '') + leaguesMap[currentLeague]?.assetBar : leaguesMap[currentLeague]?.assetBar,
      asset: nextReward?.items?.[0]?.asset && nextReward?.items?.[0]?.asset?.substring(0, 4) !== 'http' ? (routeGame.value?.imgStorageUrl || '') + nextReward.items[0].asset : nextReward.items?.[0]?.asset,
      amount: toHumanNumber(nextReward?.items?.[0]?.amount || ''),
      taken: nextReward?.taken || 0,
      req_value: nextReward?.req_value || 0
    })
  }

  const innerViewportWidth = progressOptions.value.viewportWidth - (2 * progressOptions.value.borderSegmentWidth)
  progressOptions.value.segmentWidth = Math.max(100, innerViewportWidth / (segmentsCounter.value || 1))

  segments.value = [
    leftBorderedSegment,
    ...segments.value,
    {
      width: progressOptions.value.borderSegmentWidth,
      progressMax: 0
    }
  ]
  if (progressOptions.value.isNextReward) {
    const playerData = rewardsPopup.value.loyaltyLeaguesPlayer?.[routeGame.value?.gameKey || '']
    const progressPerc = ((playerData?.leaguePoints || 0) - progressOptions.value.activeSegmentMinPoints) / progressOptions.value.activeSegmentSizePoints * 100
    const nextRewardSegment = segments.value[segments.value.length - 2]
    if (progressOptions.value.takenRewardsLength) {
      if (nextRewardSegment) {
        nextRewardSegment.progressMax = progressPerc
      }
    } else if (nextRewardSegment) {
      leftBorderedSegment.league = nextRewardSegment.league
      leftBorderedSegment.assetBar = nextRewardSegment?.assetBar || ''
      const percWidth = (leftBorderedSegment.width + progressOptions.value.segmentWidth) / 100 * progressPerc
      if (percWidth > leftBorderedSegment.width) {
        leftBorderedSegment.progressMax = 100
        nextRewardSegment.progressMax = (percWidth - leftBorderedSegment.width) / (progressOptions.value.segmentWidth) * 100
      } else {
        leftBorderedSegment.progressMax = percWidth / leftBorderedSegment.width * 100
      }
    }
  }

  progressOptions.value.visibleSegments = Math.floor(innerViewportWidth / progressOptions.value.segmentWidth)
  progressOptions.value.speedSegment = (progressOptions.value.speed / progressOptions.value.segmentWidth) * progressOptions.value.speed

  if ((trackWidth.value - 20) <= progressOptions.value.viewportWidth) {
    progressOptions.value.forceHideArrows = true
  }
  tickStepOptions.value.maxTransformX = trackWidth.value - progressOptions.value.viewportWidth
  tickStepOptions.value.animationId = requestAnimationFrame(tickStep)
}
function reBuild() {
  segments.value = []
  startPoint.value = {}
  nextLoopEvent(100).then(() => {
    progressOptions.value = initProgressOptions()
    tickStepOptions.value = initTickStepOptions()
    progressOptions.value.viewportWidth = viewport.value?.clientWidth || 0
    initSegments()
  })
}
const reBuildDebounced = debounce(reBuild, 300, { leading: false, trailing: true })

watch(() => windowWidth.value, () => {
  reBuildDebounced()
})
onBeforeUnmount(() => {
  cancelAnimationFrame(tickStepOptions.value.animationId)
  io?.disconnect()
  progressOptions.value.run = false
  reBuildDebounced.cancel?.()
})
</script>

<template>
  <div
    ref="viewport"
  >
    <div
      v-if="segmentsCounter"
      class="viewport relative overflow-hidden bg-[#1b5862] w-full mx-auto h-34"
    >
      <div
        class="absolute top-2 big:top-1.5 left-0 text-left right-0"
      >
        <div class="max-w-lg mx-auto capitalize font-black leading-tight text-base sm:text-lg big:text-xl pl-6 pr-4">
          {{ t('loyaltyProgressAnimation.rewardProgress') }}
        </div>
      </div>
      <div
        class="track absolute inset-0 flex flex-row flex-nowrap will-change-transform"
        :class="{
          'transition-transform duration-700': !progressOptions.run
        }"
        :style="{ width: `${trackWidth}px`, transform: `translateX(${tickStepOptions.xTransform}px)` }"
      >
        <div class="progress-bg border-y-4 border-white bg-linear-to-b from-[#09262a] to-[#17515a] h-6 absolute left-0 right-0 top-[3.2rem] -translate-y-1/2 z-0]" />
        <div
          v-for="(seg, index) in segments"
          :key="index"
          :ref="assignLastSeg(index)"
          class="seg h-full text-xs relative"
          :style="{
            width: `${seg.width || progressOptions.segmentWidth}px`,
            minWidth: `${seg.width || progressOptions.segmentWidth}px`
          }"
        >
          <div
            class="progress-fill h-4 absolute left-0 top-[3.2rem] -translate-y-1/2 z-1 will-change-[width] bg-contain bg-repeat-x"
            :style="`${seg?.assetBar ? `background-image: url('${seg?.assetBar}'); ` : ''}width: ${seg?.progress || 0}%`"
          />
          <div
            v-if="index === 0 && startPoint.id"
            class="h-16 flex flex-col items-center absolute right-0 top-12 translate-x-1/2 z-2"
          >
            <div class="rounded-full bg-black size-2" />
            <div class="bg-black w-0.5 h-[0.9rem]" />
            <div class="text-white p-0 w-[65px] big:w-[70px] flex items-center justify-center text-xs bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-[1.6] relative">
              <div class="absolute top-0.5 pl-0.5  mx-auto  w-full text-left text-[0.6rem] big:text-[0.8rem] gs-text-shadow font-black leading-none">
                #{{ startPoint.no }}
              </div>
              <div class="h-full w-full relative p-1.5">
                <div
                  v-if="startPoint?.asset"
                  class="bg-contain bg-no-repeat bg-center h-full w-full"
                  :style="`background-image: url('${startPoint.asset}');`"
                />
                <div
                  v-if="startPoint.taken"
                  class="absolute top-0.5 right-0.5 flex items-center justify-center bg-contain bg-no-repeat bg-center w-4 h-4"
                  :style="`background-image: url('${CheckImgUrl}');`"
                />
              </div>
              <div
                v-if="startPoint?.amount"
                class="absolute bottom-0 text-center text-[0.85rem] gs-text-shadow font-black leading-none translate-y-1/2"
              >
                x{{ startPoint.amount }}
              </div>
            </div>
          </div>
          <div
            v-if="seg.id"
            class="h-16 flex flex-col items-center absolute right-0 top-12 translate-x-1/2 z-2"
          >
            <div class="rounded-full bg-black size-2" />
            <div class="bg-black w-0.5 h-[0.9rem]" />
            <div
              class="text-white p-0 w-[65px] big:w-[70px] flex items-center justify-center text-xs bg-black/60 bg-linear-to-b from-[#041a1e] to-[#09333a] rounded-lg aspect-[1.6] relative"
              :class="{
                grayscale: !seg.taken
              }"
            >
              <div class="absolute top-0.5 pl-0.5  mx-auto  w-full text-left text-[0.6rem] big:text-[0.8rem] gs-text-shadow font-black leading-none">
                #{{ seg.no }}
              </div>
              <div class="h-full w-full relative p-1.5">
                <div
                  v-if="seg?.asset"
                  class="bg-contain bg-no-repeat bg-center h-full w-full"
                  :style="`background-image: url('${seg.asset}');`"
                />
                <div
                  v-if="seg.taken"
                  class="absolute top-0.5 right-0.5 flex items-center justify-center bg-contain bg-no-repeat bg-center w-4 h-4"
                  :style="`background-image: url('${CheckImgUrl}');`"
                />
              </div>
              <div
                v-if="seg?.amount"
                class="absolute bottom-0 text-center text-[0.85rem] gs-text-shadow font-black leading-none translate-y-1/2"
              >
                x{{ seg.amount }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!(progressOptions.run || progressOptions.forceHideArrows)"
        class="absolute left-0 right-0 bottom-0 flex items-center justify-between text-white text-lg"
      >
        <button
          class="p-2 pb-0"
          :class="{
            invisible: !(tickStepOptions.currentStepIndex >= progressOptions.visibleSegments) || tickStepOptions.xTransform >= 0
          }"
          @click="moveLeft"
        >
          <Arrow />
        </button>
        <button
          class="p-2 pb-1"
          :class="{
            invisible: lastVisible || Math.abs(tickStepOptions.xTransform) >= tickStepOptions.maxTransformX
          }"
          @click="moveRight"
        >
          <Arrow class="rotate-180" />
        </button>
      </div>
    </div>
  </div>
</template>
